import { useEffect, useState } from "react";
import Link from "next/link";

import { useUser } from "@clerk/nextjs";
import confetti from "canvas-confetti";
import { ChevronLeft, DollarSign, Users, UserStar, Zap } from "lucide-react";

import useIsMobile from "@/components/hooks/useIsMobile";
import { ReboltAppCTA } from "@/components/onboarding/DesktopWidgets";
import { StepHeader } from "@/components/onboarding/StepHeader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import type { OnboardingStepProps } from "@/types/onboarding";

export function FinalReviewStep({ onBack, canGoBack, form }: OnboardingStepProps) {
  const { user } = useUser();
  const websiteId = form.getValues("reboltWebsiteId");
  const { isMobileDevice } = useIsMobile(1024);
  const [isAppModalOpen, setIsAppModalOpen] = useState(false);

  useEffect(() => {
    if (isMobileDevice) {
      setIsAppModalOpen(true);
    }
  }, [isMobileDevice]);

  useEffect(() => {
    const duration = 3000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 4,
        angle: 90,
        spread: 120,
        origin: { x: Math.random(), y: 0 },
        colors: ["#22c55e", "#16a34a", "#15803d"],
        gravity: 0.8,
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  }, []);

  const trialFeatures = [
    {
      title: "Lead & Job Tracking",
      description: "Manage your leads and keep jobs on track.",
      icon: <Users className="w-6 h-6 shrink-0 text-rebolt-500" />,
    },
    {
      title: "Quotes & Invoices",
      description: "Create and send professional estimates within minutes.",
      icon: <DollarSign className="w-6 h-6 shrink-0 text-rebolt-500" />,
    },
    {
      title: "Automations",
      description: "Never miss following up on a quote or invoice and keep your customers updated.",
      icon: <Zap className="w-6 h-6 shrink-0 text-rebolt-500" />,
    },
    {
      title: "Reviews",
      description: "Reach more customers and grow your business.",
      icon: <UserStar className="w-6 h-6 shrink-0 text-rebolt-500" />,
    },
  ];

  const companyName = form.getValues("companyName");
  return (
    <div className="h-full flex flex-col justify-start lg:justify-center px-5 lg:px-40 py-6 lg:py-0 w-full">
      {canGoBack && (
        <Button
          type="button"
          variant="rebolt-outline"
          size="sm"
          onClick={onBack}
          className="tracking-tight w-fit lg:mb-12 mb-4 gap-2 p-2.5 lg:px-6 lg:py-2"
          iconPosition="start"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden lg:inline-block">Back</span>
        </Button>
      )}
      <StepHeader title={`Welcome, ${user?.firstName}!`} subtitle={`${companyName} is ready to grow with Rebolt.`} />

      <div className="mt-6 lg:mt-12 w-full flex flex-col gap-4">
        <p className="text-ash-700 text-sm lg:text-base tracking-tight font-semibold">Your trial includes access to:</p>
        <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-4 gap-0">
          {trialFeatures.map((feature, index) => (
            <div
              className={cn(
                "bg-white p-4 lg:rounded-2xl rounded-none flex gap-3 items-start",
                index === 0 && "rounded-t-2xl",
                index === trialFeatures.length - 1 && "rounded-b-2xl"
              )}
              key={feature.title}
            >
              {feature.icon}
              <div className="flex flex-col gap-1">
                <h3 className="text-ash-900 text-sm tracking-tight font-semibold">{feature.title}</h3>
                <p className="text-ash-500 text-xs tracking-tight font-semibold">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-6 lg:mt-12 w-full flex flex-col gap-4">
        <Link href="https://www.youtube.com/watch?v=aFjw2PHyv_4" target="_blank" rel="noreferrer noopener">
          <div className="bg-white p-2 pr-4 rounded-2xl flex gap-3 items-start select-none hover:bg-ash-100 transition-all duration-200 ease-in-out">
            <div className="aspect-video rounded-lg overflow-hidden min-w-32 w-32">
              <img src="/arwin-video.png" alt="Welcome to Rebolt" className="w-full h-full object-cover" />
            </div>
            <div className="flex flex-col gap-1">
              <h3 className="text-ash-900 lg:text-base text-sm tracking-tight font-bold leading-normal">
                Welcome to Rebolt
              </h3>
              <p className="text-ash-600 lg:text-xs text-[10px] tracking-tight font-semibold">
                Learn how to get the most out of Rebolt in 5 minutes from our cofounder Arwin.
              </p>
            </div>
          </div>
        </Link>
      </div>
      <div className="flex gap-4 lg:mt-12 mt-auto w-full">
        <Link href={`/dashboard/sites/${websiteId}/home`} className="w-full">
          <Button
            type="button"
            variant="rebolt-secondary"
            size="md"
            className="justify-center lg:max-w-fit tracking-tight lg:py-3 py-2 w-full lg:w-fit"
          >
            Go to your dashboard
          </Button>
        </Link>
      </div>

      <Dialog open={isAppModalOpen} onOpenChange={setIsAppModalOpen}>
        <DialogContent
          className="p-0 mx-auto border-none bg-transparent shadow-none font-clash"
          closeButtonClassName="text-white"
        >
          <ReboltAppCTA />
        </DialogContent>
      </Dialog>
    </div>
  );
}
